/**
 * Toast helper unifié (cf. AUDIT_BUGS_UI.md §0.2)
 *
 * Centralise l'affichage de messages utilisateur. Avant : 84 appels à
 * showToast() dispersés, 4 défs locales (scope module), résultat :
 * tous les appels hors des 4 modules définisseurs échouaient silencieusement.
 *
 * Usage :
 *   import { showToast } from './utils/toast.js';   // ou window.showToast(...)
 *   showToast('Enregistré', 'success');
 *   showToast('Erreur réseau', 'error');
 *   showToast('Astuce', 'info');
 *   showToast('Attention', 'warning');
 */

export function showToast(message, type = 'info', durationMs = 3000) {
  if (!message) return;
  const containerId = '__aslane-toast-container';
  let container = document.getElementById(containerId);
  if (!container) {
    container = document.createElement('div');
    container.id = containerId;
    container.style.cssText = 'position:fixed;bottom:18px;right:18px;z-index:var(--z-toast, 11000);display:flex;flex-direction:column;gap:8px;pointer-events:none;max-width:calc(100vw - 36px);';
    document.body.appendChild(container);
  }

  // Charte ASLANE : #D90429 (rouge brand) / #1E3A8A (bleu)
  const colors = {
    info:    '#1E3A8A',
    success: '#15803d',
    error:   '#D90429',
    warning: '#b45309',
  };
  const bg = colors[type] || colors.info;

  const toast = document.createElement('div');
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.style.cssText = `background:${bg};color:#fff;padding:10px 16px;border-radius:8px;font-family:Inter,system-ui,sans-serif;font-size:0.85rem;font-weight:600;box-shadow:0 4px 16px rgba(0,0,0,0.25);pointer-events:auto;cursor:pointer;max-width:380px;line-height:1.35;`;
  toast.textContent = String(message);
  toast.onclick = () => removeWithFade(toast);
  container.appendChild(toast);

  setTimeout(() => removeWithFade(toast), Math.max(800, durationMs));
}

function removeWithFade(el) {
  if (!el || !el.parentNode) return;
  el.style.transition = 'opacity 0.25s';
  el.style.opacity = '0';
  setTimeout(() => { try { el.remove(); } catch (_) {} }, 260);
}

// Attache à window pour que le code legacy qui appelle `showToast(...)` ou
// `window.showToast(...)` depuis des handlers inline `onclick="..."` fonctionne.
if (typeof window !== 'undefined') {
  // Ne pas écraser une définition existante (les 4 modules historiques avec leur propre showToast
  // peuvent continuer à fonctionner dans leur scope local).
  if (typeof window.showToast !== 'function') {
    window.showToast = showToast;
  }
  // Sucre additionnel pratique
  if (typeof window.showSuccess !== 'function') window.showSuccess = (m, d) => showToast(m, 'success', d);
  if (typeof window.showError   !== 'function') window.showError   = (m, d) => showToast(m, 'error', d);
  if (typeof window.showWarning !== 'function') window.showWarning = (m, d) => showToast(m, 'warning', d);
}
